import { Flame, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { addon, OptionGroup, QuantityType } from "@/types/ProductType";
import { AppConfig } from "@/utils/AppConfig";
import { addToCart } from "@/store/cart.slice";
import CashierAddons from "./CashierAddon";
import CashierOption from "./CashierOption";
import { Names } from "./OptionsAndAddonAccordion";
import { flattenOptions } from "@/utils/helpers";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

interface ModalProps {
  id: string;
  show: boolean;
  onClose: () => void;
  basePrice: number;
  lowPrice: number | undefined;
  options: OptionGroup[] | undefined;
  addons: addon[] | undefined;
  nameEN: string;
  nameAR: string;
  descriptionAR: string;
  descriptionEN: string;
  calories: number | undefined;
  ImageUrl: string;
  maxQuantity: QuantityType;
}

function CashierCardModal({
  id,
  show,
  onClose,
  basePrice,
  lowPrice,
  options,
  addons,
  nameEN,
  nameAR,
  descriptionAR,
  calories,
  descriptionEN,
  ImageUrl,
  maxQuantity,
}: ModalProps) {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const locale = i18n.language
  const [selectedOptions, setSelectedOptions] = useState<number[][]>(
    Array(options?.length).fill([]),
  );
  const [selectedAddons, setSelectedAddons] = useState<number[]>([]);
  const [optionErrors, setOptionErrors] = useState<string[]>(
    Array(options?.length).fill(""),
  );

  const [selectedOptoinsNames, setSelectedOptoinsNames] = useState<Names[][]>(
    [] as Names[][]
  );
  const [selectedAddonsNames, setSelectedAddonsNames] = useState<Names[]>(
    [] as Names[]
  );

  const [productNotes, setProductNotes] = useState<string>("");

  const dispatch = useDispatch();

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [show]);

  const lowPriceOrBasePrice = lowPrice ? lowPrice : basePrice;

  const finalPrice = useMemo(
    () =>
      lowPriceOrBasePrice +
      Object.entries(selectedOptions ?? {}).reduce<number>(
        (acc, [key, value]) =>
          acc +
          value.reduce(
            (a, c) => a + (options?.[+key]?.options?.[c]?.price ?? 0),
            0,
          ),
        0,
      ) +
      (selectedAddons ?? []).reduce<number>(
        (a, c) => a + (addons?.[c]?.price ?? 0),
        0,
      ),
    [lowPriceOrBasePrice, selectedOptions, selectedAddons, options, addons],
  );

  if (!show) return null;

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const incrementQuantity = () => {
    if (+maxQuantity == 0) {
      toast.error(
        t("cashierCard.outOfStock") as string,
      );
      return;
    } else if (maxQuantity === "infinite") {
      setQuantity(quantity + 1);
    } else if (quantity < +maxQuantity) {
      setQuantity(quantity + 1);
    } else {
      toast.error(
        t("cashierCard.maxQuantity") as string,
      );
      return;
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleAddToCart = () => {
    let hasError = false;
    const newErrors = selectedOptions?.map((arr) => {
      if (!arr.length) {
        hasError = true;
        return t("cashierCard.mustChoose") as string;
      }
      return "";
    });

    setOptionErrors(newErrors);

    if (hasError) return;

    dispatch(
      addToCart({
        _id: id,
        quantity,
        price: finalPrice,
        nameAR,
        nameEN,
        options:
          selectedOptions.map((arr) =>
            !arr.length ? [] : arr.sort((a, b) => a - b),
          ) || selectedOptions,
        addons: selectedAddons.sort((a, b) => a - b) || selectedAddons,
        addonsNames: selectedAddonsNames,
        optionsNames: flattenOptions(selectedOptoinsNames),
        image: ImageUrl,
        notes: productNotes || undefined,
        maxQuantity: maxQuantity,
      }),
    );
    onClose();
    setQuantity(1);
    setSelectedAddons([]);
    setSelectedOptions(Array(options?.length).fill([]));
    setProductNotes("");
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="relative z-50 h-[87%] w-[90%] min-w-[300px] cursor-default overflow-hidden rounded-lg bg-white shadow-lg md:h-[95%] md:w-[600px]"
        onClick={handleClick}
      >
        <div className="relative h-full overflow-hidden">
          <div className="no-scrollbar absolute inset-0 overflow-auto">
            <div className="relative">
              <img
                src={ImageUrl || ""}
                alt="Product Image"
                className="h-[450px] w-full object-cover object-[40%_40%] sm:object-[50%_40%]"
              />

              <button
                className="absolute left-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-[#375063] text-gray-300"
                onClick={onClose}
              >
                <X strokeWidth={1} />
              </button>
            </div>

            {/* Name and lowPriceOrBasePrice */}
            <div className="flex items-center justify-between px-2 pt-3">
              <span className="block font-semibold">{locale === "en" ? nameEN : nameAR}</span>

              <div dir="rtl" className="flex items-center gap-1">
                <span>
                  {lowPriceOrBasePrice.toLocaleString("en-EG", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>

                <span>{AppConfig.currency.icon()}</span>
              </div>
            </div>

            {/* description */}
            <div className="flex items-center justify-center px-2 pt-3">
              <p className="px-2 text-center text-sm text-gray-400">
                {locale === "en" ? descriptionEN : descriptionAR}
              </p>
            </div>

            {/* Calories */}
            {!!calories && (
              <div className="flex items-center justify-center px-2 pt-3">
                <p className="flex items-center justify-center gap-1 px-2 text-center">
                  <Flame size={16} />
                  <span>
                    {calories} {t("cashierCard.calories")}
                  </span>
                </p>
              </div>
            )}

            {/* Options and Addons */}
            <div>
              {!addons?.length && !options?.length && (
                <span className="block h-8 w-full"></span>
              )}
              <div>
                {options?.map((item, index) => (
                  <div key={index}>
                    <CashierOption
                      option={item}
                      onUpdate={(
                        selected: number[],
                        selectedNames: Names[]
                      ) => {
                        setSelectedOptions((prev) => {
                          const updatedOptions = [...prev];
                          updatedOptions[index] =
                            selected.length > 0 ? selected : [];
                          return updatedOptions;
                        });

                        setSelectedOptoinsNames((prev) => {
                          const updatedNames = [...prev];
                          updatedNames[index] =
                            selectedNames.length > 0 ? selectedNames : [];
                          return updatedNames;
                        });

                        setOptionErrors((prev) => {
                          const updatedErrors = [...prev];
                          updatedErrors[index] =
                            selected.length > 0
                              ? ""
                              : t("cashierCard.mustChoose") as string;
                          return updatedErrors;
                        });
                      }}
                      error={optionErrors[index]}
                    />
                  </div>
                ))}
              </div>
              <div>
                {!!addons?.length && (
                  <CashierAddons
                    addons={addons}
                    onUpdate={(selected: number[], selectedNames: Names[]) => {
                      setSelectedAddons(selected);
                      setSelectedAddonsNames(selectedNames);
                    }}
                  />
                )}
              </div>
            </div>

            {/* notes */}
            <div className="my-0 rounded-md border border-gray-100 bg-white p-4 pb-20">
              <div className="mb-2 text-[18px]">{t("cashierCard.notes")}</div>
              <textarea
                name="notes"
                id="notes"
                placeholder={
                  t("cashierCard.notesQuestion") as string
                }
                className="h-20 max-h-[200px] min-h-[60px] w-full rounded-md border p-4 focus:outline-none"
                onChange={(e) => setProductNotes(e.target.value)}
                value={productNotes}
              ></textarea>
            </div>
          </div>

          {/* footer */}

          {+maxQuantity == 0 ? (
            <div className="absolute bottom-0 left-0 flex w-full items-center justify-between gap-4 bg-white p-4 shadow-md">
              <button
                className={`flex h-10 w-full flex-wrap justify-center gap-x-2 rounded-lg bg-sidebar py-2 text-sm text-skyblue disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600 min-[370px]:flex-nowrap min-[400px]:text-base`}
                onClick={handleAddToCart}
                disabled={true}
              >
                {t("cashierCard.maxQuantity")}
              </button>
            </div>
          ) : (
            <div className="absolute bottom-0 left-0 flex w-full items-center justify-between gap-4 bg-white p-4 shadow-md">
              <div className="flex items-center rounded-md bg-gray-200">
                <button
                  className="rounded-l-lg px-4 py-2"
                  onClick={decrementQuantity}
                >
                  -
                </button>
                <div className="px-4 py-2">{quantity}</div>
                <button
                  className="rounded-r-lg px-4 py-2"
                  onClick={incrementQuantity}
                >
                  +
                </button>
              </div>
              <button
                className={`flex w-full flex-wrap justify-center gap-x-2 rounded-lg bg-sidebar py-2 text-sm text-skyblue disabled:cursor-not-allowed min-[370px]:flex-nowrap min-[400px]:text-base`}
                onClick={handleAddToCart}
                disabled={+maxQuantity === 0 ? true : false}
              >
                <span>{t("cashierCard.add")}</span>
                <div dir="rtl" className="flex gap-x-1">
                  <span> {(quantity * finalPrice).toFixed(2)} </span>
                  <span>{AppConfig.currency.icon()}</span>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CashierCardModal;
